import { Link } from "react-router-dom";
import type { LeakEntry } from "@/mocks/leaks";

const levelStyles: Record<string, string> = {
  已核验: "bg-primary-100 text-primary-800 border-primary-200",
  多方印证: "bg-accent-100 text-accent-900 border-accent-300",
  待核验: "bg-secondary-100 text-secondary-900 border-secondary-200",
  线索汇总: "bg-background-200 text-foreground-700 border-background-300",
};

type LeakCardProps = {
  entry: LeakEntry;
  layout?: "grid" | "row";
  showCover?: boolean;
};

export default function LeakCard({ entry, layout = "grid", showCover = true }: LeakCardProps) {
  const badge = levelStyles[entry.level] ?? levelStyles["线索汇总"];

  if (layout === "row") {
    return (
      <article className="group border-b border-background-200 py-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch">
          {showCover ? (
            <Link
              to={`/leaks/${entry.id}`}
              className="block w-full sm:w-44 md:w-56 h-40 sm:h-32 md:h-36 shrink-0 overflow-hidden rounded-lg bg-background-200"
            >
              <img
                src={entry.cover}
                alt={`${entry.title} - 深夜爆料线索配图`}
                title={`${entry.title} - 深夜爆料`}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </Link>
          ) : null}
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className={`rounded border px-2 py-0.5 font-label text-[11px] ${badge}`}>
                {entry.level}
              </span>
              <span className="font-label text-[11px] text-foreground-500">
                {entry.category} · {entry.code}
              </span>
            </div>
            <h3 className="font-heading text-base md:text-lg font-bold leading-snug text-foreground-950">
              <Link
                to={`/leaks/${entry.id}`}
                className="transition-colors hover:text-primary-600"
              >
                {entry.title}
              </Link>
            </h3>
            <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
              {entry.summary}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 font-label text-[11px] text-foreground-500">
              <span className="inline-flex items-center gap-1">
                <i className="ri-user-3-line" />
                {entry.source}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-time-line" />
                {entry.date} {entry.time}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-eye-line" />
                {entry.views.toLocaleString("zh-CN")}
              </span>
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      {showCover ? (
        <Link to={`/leaks/${entry.id}`} className="block h-44 overflow-hidden bg-background-200">
          <img
            src={entry.cover}
            alt={`${entry.title} - 深夜爆料线索配图`}
            title={`${entry.title} - 深夜爆料`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
      ) : null}
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className={`rounded border px-2 py-0.5 font-label text-[11px] ${badge}`}>
            {entry.level}
          </span>
          <span className="font-label text-[11px] text-foreground-500">{entry.category}</span>
        </div>
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950">
          <Link to={`/leaks/${entry.id}`} className="transition-colors hover:text-primary-600">
            {entry.title}
          </Link>
        </h3>
        <p className="mt-3 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">
          {entry.summary}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 font-label text-[11px] text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-user-3-line" />
            {entry.source}
          </span>
          <span>{entry.date}</span>
        </div>
      </div>
    </article>
  );
}